import { useCallback, useRef } from "react";
import { m } from "../../paraglide/messages.js";
import { useDark, useStill } from "../motion/motion";
import { easeInOut, phaseOf, segment } from "../motion/progress";
import { useScrollProgress } from "../motion/use-scroll-progress";
import { AppWindow } from "../scenes/app-window";
import { LinesheetGrid } from "../scenes/linesheet-grid";
import { MapRings, mapViewBox } from "./map-rings";

// The way in (ADR-286): the page opens inside one line of a linesheet, and scrolling zooms out to the line, to
// Backstage, and to the rings around it. The window keeps its size in the layout and moves by its transform alone.
const windowSize = { w: 960, h: 600 };
const zoom = 7;
const backstagePlaces = ["Home", "Linesheets", "Purchase orders", "Sales orders", "Directory"];

export function HeroZoom() {
  const still = useStill();
  const dark = useDark();
  const windowRef = useRef<HTMLDivElement>(null);
  const focusRef = useRef<HTMLSpanElement>(null);
  const trackRef = useRef<HTMLElement>(null);

  const write = useCallback((track: HTMLElement, p: number) => {
    const win = windowRef.current;
    const focus = focusRef.current;
    if (!win || !focus) return;
    const fit = Math.min((window.innerWidth - 32) / windowSize.w, (window.innerHeight * 0.56) / windowSize.h);
    const out = easeInOut(segment(p, 0, 0.55));
    const away = easeInOut(segment(p, 0.55, 0.92));
    const scale = fit * zoom ** (1 - out) * (1 - 0.7 * away);
    // the point of the window at the centre of the screen: the focused code, then the window's own centre
    const origin = offsetWithin(focus, win);
    const centre = { x: windowSize.w / 2, y: windowSize.h / 2 };
    const at = { x: origin.x + (centre.x - origin.x) * out, y: origin.y + (centre.y - origin.y) * out };
    const style = track.style;
    style.setProperty("--ox", `${origin.x}px`);
    style.setProperty("--oy", `${origin.y}px`);
    style.setProperty("--tx", `${centre.x - origin.x - scale * (at.x - origin.x)}px`);
    style.setProperty("--ty", `${centre.y - origin.y - scale * (at.y - origin.y)}px`);
    style.setProperty("--s", `${scale}`);
    style.setProperty("--q", `${away}`);
    track.dataset.phase = String(phaseOf(p, [0.25, 0.62]));
  }, []);
  useScrollProgress(trackRef, write, { still, stillAt: 0.55 });

  return (
    <section ref={trackRef} id="top" className="hero" data-still={still || undefined} aria-labelledby="hero-title">
      <div className="hero-stage">
        <svg className="hero-rings" viewBox={mapViewBox} aria-hidden>
          <MapRings withCore={false} />
        </svg>
        <div ref={windowRef} className="hero-window">
          <AppWindow app="backstage" dark={dark} title="backstage.nexxt" places={backstagePlaces}>
            <LinesheetGrid focusRef={focusRef} />
          </AppWindow>
        </div>
        <div className="hero-copy">
          <p className="hero-eyebrow">{m.hero_eyebrow()}</p>
          <h1 id="hero-title">{m.hero_title()}</h1>
          <div className="hero-captions">
            <p data-caption="0">{m.hero_caption_line()}</p>
            <p data-caption="1">{m.hero_caption_desk()}</p>
            <p data-caption="2">{m.hero_caption_out()}</p>
          </div>
          <p className="hero-scroll" aria-hidden>
            <i />
            {m.hero_scroll()}
          </p>
        </div>
      </div>
    </section>
  );
}

/** Where a descendant sits in an ancestor's own box, before any transform: its centre, in CSS pixels. */
function offsetWithin(element: HTMLElement, ancestor: HTMLElement) {
  let x = element.offsetWidth / 2;
  let y = element.offsetHeight / 2;
  let node: HTMLElement | null = element;
  while (node && node !== ancestor) {
    x += node.offsetLeft;
    y += node.offsetTop;
    node = node.offsetParent as HTMLElement | null;
  }
  return { x, y };
}
